import { useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { useLocation, useNavigate } from "react-router";
import type { TagCatalogPage, TagCatalogQuery, TagCatalogRow, TagChangeResult, TagDefinition } from "../api/generated/model";
import { BottomSheet, FeedbackScope, MenuScope, TeamMenu, useFeedback, useMenus } from "../team/TeamUi";
import { TeamWindow } from "../config/team";
import { formatText } from "../i18n/texts";
import { portalHref } from "../shell/routes";
import { BheliLoader } from "../ui/brand";
import { FilterPanel, type FilterPanelHandle } from "../ui/FilterPanel";
import { FieldError, SectionMessage } from "../ui/feedback";
import { pagerMode } from "../ui/pagination";
import { ChatIcon } from "../ui/icons/Icon";
import { RangeCalendar, dayOf, type Day } from "../ui/RangeCalendar";
import { DateInput } from "./ContactFilters";
import { ContactTagBadge } from "./ContactTagBadge";
import { contains, Highlighted } from "./searchText";
import { tagClipboardText } from "./tagClipboard";
import { ContactRequestFailure, TagCatalogClient } from "./tagCatalogClient";
import { catalogState, clearCatalogState, parseQuery, queryUrl, rememberCatalog, sameFilters } from "./tagCatalogState";
import { forNewTag, inPalette, rememberTagColors } from "./tagColor";
import { TagEditorFields, type TagDraft } from "./TagEditor";
import { count, createdLabel, createdTitle, shortDay } from "./tagFormat";
import styles from "./TagCatalogWorkspace.module.css";

// Chat › Configuración › Etiquetas (Etiquetas R1, kit 1.14.3): encabezado con pestañas, buscador y píldoras de estado,
// «Más filtros» con el rango de creación, tabla sin caja (C28) con su barra, selección (C29), menú de fila y archivar con
// Deshacer; en el celular, lista C17 con hojas inferiores. Los permisos los decide el servidor: la pantalla solo ofrece lo
// que el contexto dice que se puede, y cada pedido vuelve a comprobarlo (TagCatalogClient).

type Props = {
  labels: Record<string, string>;
  locale: string;
  tenant: number;
  user: number;
};

const mobileQuery = "(max-width: 767px)";
const subscribeMobile = (changed: () => void) => {
  const media = matchMedia(mobileQuery);
  media.addEventListener("change", changed);
  return () => media.removeEventListener("change", changed);
};
/** A 767 px o menos no hay nada de escritorio (E83): lista, hojas y pantallas completas. */
const useMobile = () => useSyncExternalStore(subscribeMobile, () => matchMedia(mobileQuery).matches);

const pressed = (value: boolean) => (value ? "true" : "false");
const todayDay = (): Day => {
  const now = new Date();
  return dayOf(now.getFullYear(), now.getMonth() + 1, now.getDate());
};
const showDay = (day: Day | null, english: boolean) => {
  if (!day) return "";
  const [year, month, date] = day.split("-");
  return english ? `${month}/${date}/${year}` : `${date}/${month}/${year}`;
};
/** dd/mm/aaaa (o mm/dd/aaaa en inglés) escrito a mano → aaaa-mm-dd; null si no es una fecha válida. */
const parseDay = (text: string, english: boolean): Day | null => {
  const match = /^\s*(\d{1,2})\/(\d{1,2})\/(\d{4})\s*$/.exec(text);
  if (!match) return null;
  const [first, second, year] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const [month, date] = english ? [first, second] : [second, first];
  const value = new Date(year, month - 1, date);
  return value.getMonth() === month - 1 && value.getDate() === date ? dayOf(year, month, date) : null;
};

export function TagCatalogWorkspace(props: Props) {
  // El toast de FeedbackScope se dibuja fijo abajo solo dentro de .tm (team.css): el ámbito va dentro del marco.
  return (
    <div className="tm tg-root">
      <MenuScope>
        <FeedbackScope undoLabel={props.labels["C1_UNDO"] ?? ""}>
          <Workspace {...props} />
        </FeedbackScope>
      </MenuScope>
    </div>
  );
}

function Workspace({ labels, locale, tenant, user }: Props) {
  const L = useCallback((key: string) => labels[`E1_${key}`] ?? `[E1_${key}]`, [labels]);
  const C = useCallback((key: string) => labels[`C1_${key}`] ?? `[C1_${key}]`, [labels]);
  const s = styles.scope;
  const english = locale.toLowerCase().startsWith("en");
  const mobile = useMobile();
  const location = useLocation();
  const navigate = useNavigate();
  const feedback = useFeedback();
  const menus = useMenus();
  const state = catalogState(tenant, user);
  const routeQuery = useMemo(() => parseQuery(location.search), [location.search]);
  // En el celular la lista carga de 25 en 25 al bajar: la página y el tamaño de la dirección no aplican.
  const query = useMemo(
    () => (mobile ? { ...routeQuery, page: 1, pageSize: 25 } : routeQuery),
    [mobile, routeQuery],
  );
  const api = useMemo(() => new TagCatalogClient(tenant, user), [tenant, user]);
  const [page, setPage] = useState<TagCatalogPage | null>(null);
  // C06 · lo que se está escribiendo antes de que responda el servidor: filtra al instante las filas ya cargadas.
  const [typing, setTyping] = useState<string | null>(null);
  const [rows, setRows] = useState<TagCatalogRow[]>([]);
  const [busy, setBusy] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreFailed, setMoreFailed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState(() => new Map(state.selected));
  const [selecting, setSelecting] = useState(false);
  const [editor, setEditor] = useState<{ tag: TagDefinition | null } | null>(null);
  const [draft, setDraft] = useState<TagDraft>({ name: "", color: null, description: "" });
  const [original, setOriginal] = useState("");
  const [nameError, setNameError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [duplicateArchived, setDuplicateArchived] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [extractBusy, setExtractBusy] = useState(false);
  const [copyFallback, setCopyFallback] = useState<{ text: string; count: number; fromSelection: boolean } | null>(null);
  const copyOpener = useRef<HTMLElement | null>(null);
  const root = useRef<HTMLElement>(null);
  const nameField = useRef<HTMLInputElement>(null);
  const filterPanel = useRef<FilterPanelHandle>(null);
  const pillRow = useRef<HTMLDivElement>(null);
  const searchInput = useRef<HTMLInputElement>(null);
  const revision = useRef(0);
  const fetcher = useRef<AbortController | null>(null);
  const work = useRef({ busy, saving, extractBusy });
  useEffect(() => {
    work.current = { busy, saving, extractBusy };
  });

  const sync = (next: Map<number, TagDefinition>) => {
    rememberCatalog({ selected: next });
    setSelected(new Map(next));
    if (next.size === 0) setSelecting(false);
  };
  const failure = useCallback(
    (exception: unknown) => {
      const code = exception instanceof ContactRequestFailure ? exception.code : "";
      const map: Record<string, string> = {
        TAG_DUPLICATE: "TAG_DUPLICATE",
        TAG_DUPLICATE_ARCHIVED: "TAG_DUPLICATE_ARCHIVED",
        TAG_ARCHIVED: "TAG_ARCHIVED",
        TAG_NOT_FOUND: "TAG_NOT_FOUND",
        STALE_VERSION: "STALE",
        INVALID_REQUEST: "INVALID",
        CRM_FORBIDDEN: "DENIED",
        SESSION_INVALID: "DENIED",
        TENANT_CHANGED: "DENIED",
      };
      return L(map[code] ?? "UNAVAILABLE");
    },
    [L],
  );

  /** Pide la página `current`; `append` suma filas (lista del celular) en lugar de reemplazarlas. */
  const fetchPage = useCallback(
    (current: TagCatalogQuery, append = false): Promise<void> => {
      const ticket = ++revision.current;
      // C06: cada pedido nuevo cancela el anterior; la respuesta tardía se descarta por ticket.
      fetcher.current?.abort();
      const controller = new AbortController();
      fetcher.current = controller;
      return api.list(current, controller.signal).then(
        (result) => {
          if (ticket !== revision.current || controller.signal.aborted) return;
          const stored = catalogState(tenant, user);
          if (stored.sessionStamp !== null && stored.sessionStamp !== api.currentSessionStamp) sync(new Map());
          rememberCatalog({ sessionStamp: api.currentSessionStamp });
          setPage(result);
          rememberTagColors(result.items.map((row) => row.tag));
          setTyping(null);
          setRows((previous) => (append ? [...previous, ...result.items] : result.items));
          const refreshed = new Map(catalogState(tenant, user).selected);
          for (const row of result.items) if (refreshed.has(row.tag.id)) refreshed.set(row.tag.id, row.tag);
          rememberCatalog({ selected: refreshed });
          setSelected(new Map(refreshed));
          setBusy(false);
          setLoadingMore(false);
        },
        (exception: unknown) => {
          if (ticket !== revision.current || controller.signal.aborted) return;
          if (exception instanceof DOMException && exception.name === "AbortError") return;
          if (append) {
            setMoreFailed(true);
            setLoadingMore(false);
            return;
          }
          setPage(null);
          setRows([]);
          setError(failure(exception));
          sync(new Map());
          if (
            exception instanceof ContactRequestFailure &&
            (exception.code === "TENANT_CHANGED" || exception.code === "SESSION_INVALID")
          )
            clearCatalogState();
          setBusy(false);
        },
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [api, failure, tenant, user],
  );
  const reload = useCallback(() => {
    setError(null);
    return fetchPage(query);
  }, [fetchPage, query]);

  // La consulta vive en la dirección: cada cambio vuelve a leer (otro filtro limpia la selección).
  const [seenQuery, setSeenQuery] = useState<TagCatalogQuery | null>(null);
  if (seenQuery !== query) {
    setSeenQuery(query);
    if (!sameFilters(query, state.query)) {
      rememberCatalog({ selected: new Map() });
      setSelected(new Map());
      setSelecting(false);
    }
    rememberCatalog({ query: routeQuery });
    setBusy(true);
    setError(null);
    setMoreFailed(false);
  }
  useEffect(() => {
    void fetchPage(query);
  }, [fetchPage, query]);

  // Al volver a la pestaña o a la ventana se refresca, como hoy.
  useEffect(() => {
    const refresh = () => {
      if (!work.current.busy && !work.current.saving && !work.current.extractBusy && !mobile) void reload();
    };
    const visible = () => {
      if (!document.hidden) refresh();
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", visible);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", visible);
    };
  }, [reload, mobile]);

  const changeQuery = (next: TagCatalogQuery, clearSelection = true) => {
    if (clearSelection) sync(new Map());
    rememberCatalog({ query: next });
    navigate(queryUrl(next), { replace: true });
  };
  const current = state.query;
  const search = current.search?.trim() ? current.search.trim() : null;
  /** Término que se marca y filtra: el escrito mientras el servidor no respondió; si no, el de la URL. */
  const term = typing !== null ? typing.trim() || null : search;
  const shown = (items: TagCatalogRow[]) =>
    typing === null || term === null ? items : items.filter((row) => contains(row.tag.name, term) || contains(row.tag.description, term));
  const hasDates = !!current.from || !!current.to;
  const access = page?.access;
  const canManage = access?.canManageTags === true;
  const canContacts = access?.canRead === true;
  const canExportContacts = canContacts && access?.canExport === true;

  // ---- Lista larga del celular: carga sola al bajar, pidiendo las siguientes cuando faltan 10 ----
  // I11 · el aviso vive en la décima fila antes del final: al verse, se piden las siguientes.
  const sentinel = useRef<HTMLLIElement>(null);
  const hasMore = mobile && page !== null && rows.length < page.total;
  useEffect(() => {
    if (!hasMore || loadingMore || moreFailed) return;
    const node = sentinel.current;
    if (!node) return;
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      setLoadingMore(true);
      void fetchPage({ ...query, page: (page?.page ?? 1) + 1 }, true);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, moreFailed, fetchPage, query, page]);
  const retryMore = () => {
    setMoreFailed(false);
    setLoadingMore(true);
    void fetchPage({ ...query, page: (page?.page ?? 1) + 1 }, true);
  };

  // ---- Contactos de una etiqueta: con «Cualquier fecha», para que el número sea el mismo de la celda (I7) ----
  const contactsUrl = (tag: TagDefinition) =>
    `/portal/chat/contactos?tag=${encodeURIComponent(tag.name)}&period=ANY&returnTags=${encodeURIComponent(queryUrl(current))}`;

  // ---- Nueva y Editar ----
  const draftValue = (value: TagDraft) => `${value.name}\n${value.color ?? ""}\n${value.description}`;
  const openEditor = async (tag: TagDefinition | null) => {
    menus.setOpen(null);
    let color = tag?.color ?? null;
    if (tag && color) color = inPalette(color)?.fill ?? color;
    if (!tag) {
      // El color que sigue al de la última etiqueta creada en la empresa (activa o archivada).
      try {
        const last = await api.list({ ...current, state: "ALL", search: null, from: null, to: null, page: 1, pageSize: 10, sort: "CREATED", reverse: false });
        color = forNewTag(last.items[0]?.tag.color ?? null);
      } catch {
        color = forNewTag(null);
      }
    }
    const next = { name: tag?.name ?? "", color, description: tag?.description ?? "" };
    setDraft(next);
    setOriginal(draftValue(next));
    setNameError(null);
    setServerError(null);
    setDuplicateArchived(false);
    setEditor({ tag });
  };
  const closeEditor = () => {
    if (saving) return;
    // Sin cambios cierra directo; con cambios, la confirmación de C12.
    if (draftValue(draft) === original) setEditor(null);
    else setDiscarding(true);
  };
  const editDraft = (next: TagDraft) => {
    setDraft(next);
    if (nameError && next.name.trim()) setNameError(null);
  };
  const save = async () => {
    if (saving || !editor) return;
    setServerError(null);
    const trimmed = draft.name.trim();
    if (!trimmed) {
      setNameError(L("NAME_REQUIRED"));
      nameField.current?.focus();
      return;
    }
    if (trimmed.length > 50 || /[\u0000-\u001f\u007f-\u009f]/.test(draft.name)) {
      setNameError(L("R1_NAME_TOO_LONG"));
      nameField.current?.focus();
      return;
    }
    if (draft.description.length > 200 || /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f-\u009f]/.test(draft.description)) {
      setServerError(L("INVALID"));
      return;
    }
    setSaving(true);
    try {
      await api.save(editor.tag?.id ?? null, {
        name: draft.name,
        color: draft.color,
        description: draft.description,
        expectedVersion: editor.tag?.rowVersion ?? null,
      });
      const created = editor.tag === null;
      setEditor(null);
      feedback.show(created ? formatText(L("R1_CREATED_TOAST"), trimmed) : L("SAVED"));
      await reload();
    } catch (exception) {
      const code = exception instanceof ContactRequestFailure ? exception.code : "";
      setDuplicateArchived(code === "TAG_DUPLICATE_ARCHIVED");
      if (code === "INVALID_REQUEST" && trimmed.length > 50) setNameError(L("R1_NAME_TOO_LONG"));
      else setServerError(failure(exception));
    } finally {
      setSaving(false);
    }
  };
  const showArchived = () => {
    setEditor(null);
    changeQuery({ ...current, state: "ARCHIVED", search: null, page: 1 });
  };

  // ---- Archivar y restaurar: al instante, con Deshacer (Guía 2.5) ----
  const changeState = async (tags: TagDefinition[], archived: boolean, undoable = true) => {
    const changes = tags
      .filter((tag) => tag.archived !== archived)
      .map((tag) => ({ id: tag.id, expectedVersion: tag.rowVersion, archived }));
    if (changes.length === 0 || saving) return;
    menus.setOpen(null);
    setSaving(true);
    try {
      const result: TagChangeResult[] = await api.state(changes);
      const done = result.filter((row) => row.result === "SAVED" && row.tag).map((row) => row.tag!);
      sync(new Map());
      await reload();
      if (done.length === 0) {
        setError(L("STALE"));
        return;
      }
      const one = done.length === 1;
      const message = archived
        ? one
          ? formatText(L("R1_ARCHIVED_ONE"), done[0]!.name)
          : formatText(L("R1_ARCHIVED_MANY"), done.length)
        : one
          ? formatText(L("R1_RESTORED_ONE"), done[0]!.name)
          : formatText(L("R1_RESTORED_MANY"), done.length);
      feedback.show(message, undoable ? () => changeState(done, !archived, false) : undefined);
    } catch (exception) {
      setError(failure(exception));
    } finally {
      setSaving(false);
    }
  };

  // ---- Copiar y exportar ----
  /** C46 · aviso de copiado; desde la barra de selección, además, la selección se vacía (C29). */
  const copied = (copy: { count: number; fromSelection: boolean }) => {
    feedback.show(copy.count === 1 ? L("R1_COPIED_ONE") : formatText(L("R1_COPIED_MANY"), count(copy.count)));
    if (copy.fromSelection) sync(new Map());
  };
  const copy = async () => {
    if (extractBusy) return;
    menus.setOpen(null);
    // El foco vuelve al botón que abrió la acción al cerrar la ventana de respaldo (el ⋮ si la opción vino de su menú).
    const active = document.activeElement;
    copyOpener.current = active?.closest(".tm-menu")?.querySelector<HTMLElement>("summary, button") ?? (active as HTMLElement | null);
    setExtractBusy(true);
    const marked = [...state.selected.keys()];
    try {
      // Todas las páginas de lo filtrado (o lo marcado), de 100 en 100, con el mismo formateador de C46.
      const rows: TagCatalogRow[] = [];
      for (let number = 1; ; number++) {
        const result = await api.list({ ...current, page: number, pageSize: 100, selectedIds: marked.length > 0 ? marked : null });
        rows.push(...result.items);
        if (number * 100 >= result.total || result.items.length === 0) break;
      }
      const next = { text: tagClipboardText(rows, L, locale), count: rows.length, fromSelection: marked.length > 0 };
      setCopyFallback(null);
      try {
        await navigator.clipboard.writeText(next.text);
        copied(next);
      } catch {
        setCopyFallback(next);
      }
    } catch (exception) {
      setError(failure(exception));
    } finally {
      setExtractBusy(false);
    }
  };
  const download = async () => {
    if (extractBusy) return;
    setExtractBusy(true);
    try {
      // D1: Exportar ignora la selección y baja todo lo que dejan la búsqueda y los filtros.
      const text = await api.extract({ query: { ...current, page: 1, selectedIds: null }, format: "CSV" });
      const url = URL.createObjectURL(new Blob(["﻿", text], { type: "text/plain;charset=utf-8" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = "etiquetas.csv";
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setExporting(false);
    } catch (exception) {
      setError(failure(exception));
    } finally {
      setExtractBusy(false);
    }
  };
  const openColumns = () => {
    menus.setOpen(null);
    root.current?.querySelector<HTMLButtonElement>("[data-report-launcher] button")?.click();
  };

  // ---- Selección ----
  const select = (tag: TagDefinition, value: boolean) => {
    const next = new Map(state.selected);
    if (value) next.set(tag.id, tag);
    else next.delete(tag.id);
    sync(next);
  };
  const visible = mobile ? rows : (page?.items ?? []);
  const pageMarked = visible.length > 0 && visible.every((row) => selected.has(row.tag.id));
  const someMarked = visible.some((row) => selected.has(row.tag.id));
  const selectPage = () => {
    const next = new Map(state.selected);
    for (const row of visible) {
      if (pageMarked) next.delete(row.tag.id);
      else next.set(row.tag.id, row.tag);
    }
    sync(next);
  };
  useEffect(() => {
    if (selected.size === 0) return;
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !document.querySelector("dialog[open]")) sync(new Map());
    };
    document.addEventListener("keydown", escape);
    return () => document.removeEventListener("keydown", escape);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected.size]);
  const markedActive = [...selected.values()].filter((tag) => !tag.archived);
  const markedArchived = [...selected.values()].filter((tag) => tag.archived);

  // ---- Orden por títulos (escritorio) ----
  const sortBy = (sort: string) =>
    changeQuery({ ...current, sort, reverse: current.sort === sort ? !current.reverse : false, page: 1 }, false);
  const sortState = (sort: string): "ascending" | "descending" | "none" => {
    if (current.sort !== sort) return "none";
    const natural = sort === "NAME" ? "ascending" : "descending";
    return current.reverse ? (natural === "ascending" ? "descending" : "ascending") : natural;
  };
  const sortHeader = (sort: string, label: string, className: string) => {
    const direction = sortState(sort);
    return (
      <th
        className={className}
        aria-sort={direction}
        data-column-id={sort === "NAME" ? "TITLE" : sort === "USED" ? "CONTACTS" : "CREATED"}
        data-column-required={sort === "NAME" ? "true" : undefined}
      >
        <button type="button" className="tg-sort" onClick={() => sortBy(sort)}>
          {label}
          {direction !== "none" ? <ChatIcon name={direction === "ascending" ? "arrow-up" : "arrow-down"} /> : null}
        </button>
      </th>
    );
  };

  // ---- Fechas del filtro ----
  const dateTitle = hasDates
    ? formatText(L("R1_DATE_TITLE"), rangeText(current.from, current.to).split(" – ")[0]!, rangeText(current.from, current.to).split(" – ")[1]!)
    : "";

  // ---- Piezas ----
  const rowOptions = (tag: TagDefinition) => {
    const options: { key: string; icon: string; label: string; run: () => void; separator?: boolean; href?: string }[] = [];
    if (canManage) options.push({ key: "edit", icon: "pencil", label: L("EDIT"), run: () => void openEditor(tag) });
    if (canExportContacts)
      options.push({ key: "export", icon: "download", label: L("EXPORT_CONTACTS"), run: () => {}, href: portalHref(`${contactsUrl(tag)}&extract=1`) });
    if (mobile && canContacts)
      options.push({ key: "contacts", icon: "contact", label: L("R1_VIEW_CONTACTS"), run: () => {}, href: portalHref(contactsUrl(tag)) });
    if (canManage)
      options.push({
        key: "state",
        icon: tag.archived ? "archive-restore" : "archive",
        label: L(tag.archived ? "R1_RESTORE" : "R1_ARCHIVE"),
        run: () => void changeState([tag], !tag.archived),
        separator: options.length > 0,
      });
    return options;
  };
  const rowMenu = (tag: TagDefinition) => {
    const options = rowOptions(tag);
    if (options.length === 0) return null;
    const label = formatText(L("R1_ROW_MORE"), tag.name);
    return (
      <TeamMenu
        id={`tag-${tag.id}`}
        className="tm-menu tg-row-menu"
        triggerClass="tm-icon-btn tg-row-more"
        ariaLabel={label}
        tooltip={label}
        summary={<ChatIcon name="ellipsis-vertical" />}
      >
        <div className="tm-menu-items" role="menu" aria-label={label}>
          <BottomSheet>
            {mobile ? (
              <div className="tg-sheet-tag">
                <ContactTagBadge name={tag.name} color={tag.color} />
              </div>
            ) : null}
            {options.map(({ key, ...option }) => (
              <MenuOption key={key} {...option} />
            ))}
          </BottomSheet>
        </div>
      </TeamMenu>
    );
  };

  const header = (
    <>
      <header className="tm-head tg-head" data-part="encabezado">
        <div className="tm-head-title">
          <a
            className="tm-back tg-back"
            href={portalHref("/portal/chat/configuracion")}
            aria-label={L("R1_BACK")}
            title={L("R1_BACK")}
          >
            <ChatIcon name="arrow-left" />
          </a>
          <h1>{L("TITLE")}</h1>
        </div>
        {canManage ? (
          mobile ? (
            <button
              type="button"
              className="tm-btn primary tg-plus"
              aria-label={L("NEW")}
              title={L("NEW")}
              onClick={() => void openEditor(null)}
            >
              <ChatIcon name="plus" />
            </button>
          ) : (
            <button type="button" className="tm-btn primary" onClick={() => void openEditor(null)}>
              <ChatIcon name="plus" />
              {L("NEW")}
            </button>
          )
        ) : null}
      </header>
      <p className="tg-sub">{L("SHARED_BASE")}</p>
    </>
  );

  const statePills = (
    <div className="tm-pills tg-state" role="group" aria-label={L("STATE")}>
      {["ACTIVE", "ARCHIVED", "ALL"].map((value) => (
        <button
          key={value}
          type="button"
          className="tm-pill"
          aria-pressed={pressed((current.state ?? "ACTIVE") === value)}
          onClick={() => {
            if ((current.state ?? "ACTIVE") !== value) changeQuery({ ...current, state: value, page: 1 });
          }}
        >
          {L(value)}
        </button>
      ))}
    </div>
  );

  // K3 · C07 1.14.5: en el celular, al cargar y al aplicar, si la píldora de la fecha aplicada no se ve entera, la fila
  // se desliza lo justo para que termine a 24 px del borde derecho; si no, empieza en 0. Abrir o cerrar «Más filtros»
  // no la mueve (FilterPanel conserva su scroll). Sin animación: scrollLeft directo.
  useLayoutEffect(() => {
    const row = pillRow.current;
    if (!row) return;
    const applied = mobile ? row.querySelector<HTMLElement>(".tg-date-pill") : null;
    // Sin una fecha aplicada en el celular no se lee la geometría (leerla forzaba el layout en cada carga).
    if (!applied) {
      if (row.scrollLeft !== 0) row.scrollLeft = 0;
      row.removeAttribute("data-scrolled");
      return;
    }
    row.scrollLeft = 0;
    const overflow = applied.getBoundingClientRect().right - (innerWidth - 24);
    if (overflow > 0) row.scrollLeft = overflow;
    row.toggleAttribute("data-scrolled", row.scrollLeft > 0);
  }, [mobile, current.from, current.to, page]);

  const filters = (
    <div className="tg-filters" data-filter-anchor="" data-part="filtros">
      <TagSearch
        inputRef={searchInput}
        value={current.search ?? ""}
        placeholder={L("SEARCH")}
        clearLabel={labels["C1_CLEAR_SEARCH"] ?? L("CLEAR")}
        onTyping={setTyping}
        onChange={(value) => changeQuery({ ...current, search: value || null, page: 1 })}
      />
      <div
        className="tg-pill-row"
        ref={pillRow}
        onScroll={(event) => event.currentTarget.toggleAttribute("data-scrolled", event.currentTarget.scrollLeft > 0)}
      >
        {statePills}
        <span className="tg-divider" aria-hidden="true"></span>
        {hasDates ? (
          <button
            type="button"
            className="tm-pill tg-date-pill"
            aria-pressed="true"
            title={dateTitle}
            aria-label={dateTitle}
            onClick={() => changeQuery({ ...current, from: null, to: null, page: 1 })}
          >
            <ChatIcon name="calendar" />
            {rangeText(current.from, current.to)}
          </button>
        ) : null}
        <DateFilter
          ref={filterPanel}
          query={current}
          english={english}
          mobile={mobile}
          C={C}
          L={L}
          onApply={(from, to) => changeQuery({ ...current, from, to, page: 1 })}
          onClear={() => changeQuery({ ...current, state: "ACTIVE", from: null, to: null, page: 1 })}
        />
      </div>
    </div>
  );

  const total = page?.total ?? 0;
  const empty = page !== null && !error && total === 0;
  // J3 · C06 1.14.4: con búsqueda y la lista vacía en Activas o Archivadas, se pregunta cuántas coinciden en el otro
  // estado con la misma búsqueda y los mismos filtros (fecha incluida). Se cancela si cambia la consulta.
  const currentState = current.state ?? "ACTIVE";
  const otherState = currentState === "ACTIVE" ? "ARCHIVED" : currentState === "ARCHIVED" ? "ACTIVE" : null;
  const hiddenKey = JSON.stringify([search, otherState, current.from ?? null, current.to ?? null]);
  const [hidden, setHidden] = useState<{ key: string; total: number } | null>(null);
  const checkHidden = empty && !busy && search !== null && otherState !== null;
  useEffect(() => {
    if (!checkHidden || !otherState) return;
    const controller = new AbortController();
    api
      .list({ ...current, state: otherState, page: 1, pageSize: 10, selectedIds: null }, controller.signal)
      .then(
        (result) => {
          if (!controller.signal.aborted) setHidden({ key: hiddenKey, total: result.total });
        },
        () => {
          if (!controller.signal.aborted) setHidden({ key: hiddenKey, total: 0 });
        },
      );
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkHidden, hiddenKey, api]);
  const hiddenTotal = hidden?.key === hiddenKey ? hidden.total : null;
  const hiddenLine = (n: number, q: string) =>
    formatText(L(`R2_HIDDEN_${otherState}_${n === 1 ? "ONE" : "MANY"}`), count(n), q);
  const emptyState = empty ? (
    search && otherState && hiddenTotal === null ? (
      // Mientras responde, solo el título: nunca un «No hay etiquetas…» que después cambia (E88).
      <div className="tm-empty tg-empty" role="status" data-part="vacio">
        <strong>{L("R1_NO_RESULTS")}</strong>
      </div>
    ) : search && otherState && hiddenTotal !== null && hiddenTotal > 0 ? (
      <div className="tm-empty tg-empty" role="status" data-part="vacio">
        <strong>{L("R1_NO_RESULTS")}</strong>
        <span>{hiddenLine(hiddenTotal, search)}</span>
        <button
          type="button"
          className="tm-btn secondary"
          onClick={() => changeQuery({ ...current, state: otherState, page: 1 })}
        >
          {L(otherState === "ARCHIVED" ? "R2_VIEW_ARCHIVED" : "R2_VIEW_ACTIVE")}
        </button>
      </div>
    ) : search ? (
      <div className="tm-empty tg-empty" role="status" data-part="vacio">
        <strong>{L("R1_NO_RESULTS")}</strong>
        <span>{formatText(L("R1_NO_MATCH"), search)}</span>
        <button
          type="button"
          className="tm-btn secondary"
          onClick={() => {
            changeQuery({ ...current, search: null, page: 1 });
            requestAnimationFrame(() => searchInput.current?.focus());
          }}
        >
          {L("R1_CLEAR_SEARCH")}
        </button>
      </div>
    ) : (
      <div className="tm-empty tg-empty" role="status" data-part="vacio">
        <strong>{L("R1_NO_RESULTS")}</strong>
        {(current.state ?? "ACTIVE") !== "ACTIVE" || hasDates ? (
          <button
            type="button"
            className="tm-btn secondary"
            onClick={() => changeQuery({ ...current, state: "ACTIVE", from: null, to: null, page: 1 })}
          >
            {C("CLEAR_FILTERS")}
          </button>
        ) : null}
      </div>
    )
  ) : null;

  const countText = total === 1 ? L("R1_COUNT_ONE") : formatText(L("R1_COUNT_MANY"), count(total));
  const selectedText = selected.size === 1 ? L("R1_SELECTED_ONE") : formatText(L("SELECTED"), selected.size);

  const tableMenu = (
    <TeamMenu
      id="tags-table"
      className="tm-menu"
      triggerClass="tm-icon-btn"
      ariaLabel={L("R1_TABLE_MORE")}
      tooltip={L("R1_TABLE_MORE")}
      summary={<ChatIcon name="ellipsis-vertical" />}
    >
      <div className="tm-menu-items" role="menu" aria-label={L("R1_TABLE_MORE")}>
        <BottomSheet>
          {mobile ? (
            <>
              <MenuOption icon="arrow-up-down" label={C("SORT_BY")} detail={sortSummary(current, L, C)} run={() => menus.setOpen("tags-sort")} />
              <MenuOption icon="download" label={L("EXPORT")} run={() => {
                menus.setOpen(null);
                setExporting(true);
              }} />
            </>
          ) : null}
          <MenuOption icon="copy" label={L("COPY")} run={() => void copy()} />
          <MenuOption icon="columns" label={mobile ? L("R1_COLUMNS").replace(/…$/, "") : L("R1_COLUMNS")} run={openColumns} />
          {mobile ? (
            <MenuOption icon="square-check" label={L("R1_SELECT")} run={() => {
              menus.setOpen(null);
              setSelecting(true);
            }} />
          ) : null}
        </BottomSheet>
      </div>
    </TeamMenu>
  );

  const selectionBar = (
    <div className="tg-bar tg-selection" role="toolbar" aria-label={selectedText} data-part="seleccion">
      <button
        type="button"
        className="tm-icon-btn"
        aria-label={L("CLEAR_SELECTION")}
        title={L("CLEAR_SELECTION")}
        onClick={() => sync(new Map())}
      >
        <ChatIcon name="x" />
      </button>
      <strong>{selectedText}</strong>
      {mobile ? (
        // J10 · C29 1.14.4: en el celular no hay tooltip, así que las acciones van con su texto en la hoja del ⋮ (E91).
        <TeamMenu
          id="tags-selection"
          className="tm-menu tg-selection-menu"
          triggerClass="tm-icon-btn tg-selection-more"
          ariaLabel={L("R2_SELECTION_ACTIONS")}
          tooltip={L("R2_SELECTION_ACTIONS")}
          summary={<ChatIcon name="ellipsis-vertical" />}
        >
          <div className="tm-menu-items" role="menu" aria-label={L("R2_SELECTION_ACTIONS")}>
            <BottomSheet>
              <p className="tg-sheet-count">{selectedText}</p>
              {canManage && markedActive.length > 0 ? (
                <MenuOption icon="archive" label={L("ARCHIVE")} run={() => void changeState(markedActive, true)} />
              ) : null}
              {canManage && markedArchived.length > 0 ? (
                <MenuOption icon="archive-restore" label={L("RESTORE")} run={() => void changeState(markedArchived, false)} />
              ) : null}
              <MenuOption icon="copy" label={L("COPY")} run={() => void copy()} />
            </BottomSheet>
          </div>
        </TeamMenu>
      ) : null}
      <span className="tg-bar-actions" hidden={mobile}>
        {canManage && markedActive.length > 0 ? (
          <button type="button" className="tg-bar-btn" onClick={() => void changeState(markedActive, true)}>
            <ChatIcon name="archive" />
            {L("ARCHIVE")}
          </button>
        ) : null}
        {canManage && markedArchived.length > 0 ? (
          <button type="button" className="tg-bar-btn" onClick={() => void changeState(markedArchived, false)}>
            <ChatIcon name="archive-restore" />
            {L("RESTORE")}
          </button>
        ) : null}
        <button type="button" className="tg-bar-btn" onClick={() => void copy()}>
          <ChatIcon name="copy" />
          {L("COPY")}
        </button>
      </span>
    </div>
  );

  const tableBar =
    selected.size > 0 || (mobile && selecting) ? (
      selected.size > 0 ? selectionBar : <div className="tg-bar">{countText}</div>
    ) : (
      <div className="tg-bar" data-part="barra">
        <span className="tg-count" role="status">
          {countText}
        </span>
        <span className="tg-bar-end">
          {!mobile ? (
            <button type="button" className="tm-btn secondary small tg-export" onClick={() => setExporting(true)}>
              <ChatIcon name="download" />
              {L("EXPORT")}
            </button>
          ) : null}
          {tableMenu}
        </span>
      </div>
    );

  const contactCell = (row: TagCatalogRow) => {
    const contacts = row.contacts ?? 0;
    if (contacts === 0) return <span className="tg-zero">0</span>;
    const title =
      contacts === 1
        ? formatText(L("R1_CONTACTS_ONE"), row.tag.name)
        : formatText(L("R1_CONTACTS_MANY"), count(contacts), row.tag.name);
    return (
      <a className="tg-count-link" href={portalHref(contactsUrl(row.tag))} title={title} aria-label={title}>
        {count(contacts)}
      </a>
    );
  };

  const checkbox = (checked: boolean, mixed: boolean, label: string, onToggle: () => void) => (
    <button
      type="button"
      role="checkbox"
      className="tg-check"
      aria-checked={mixed ? "mixed" : pressed(checked)}
      aria-label={label}
      onClick={(event) => {
        event.stopPropagation();
        onToggle();
      }}
    >
      {mixed ? <ChatIcon name="minus" /> : checked ? <ChatIcon name="check" /> : null}
    </button>
  );

  // Encabezados de la tabla; «Columnas y vistas» (ReportViews) los lee para su ventana, también en el celular.
  const headerRow = (
    <tr>
      <th className="tg-col-check" data-column-id="SELECT_PAGE" data-column-required="true" data-column-system="true">
        {checkbox(pageMarked, someMarked && !pageMarked, L("SELECT_PAGE"), selectPage)}
      </th>
      {sortHeader("NAME", L("TITLE"), "tg-col-tag")}
      <th className="tg-col-description" data-column-id="DESCRIPTION">
        {L("DESCRIPTION")}
      </th>
      {canContacts ? sortHeader("USED", L("CONTACTS"), "tg-col-contacts") : null}
      {sortHeader("CREATED", L("CREATED"), "tg-col-created")}
      {rowOptionsAvailable(canManage, canExportContacts) ? (
        <th className="tg-col-action" data-column-id="EDIT" data-column-required="true" data-column-system="true">
          <span className="tm-sr">{L("EDIT")}</span>
        </th>
      ) : null}
    </tr>
  );
  const columnsLauncher = (
    <div className="report-view-tools tg-columns-launcher" data-report-launcher="company.tagcatalogworkspace.t1"></div>
  );
  const table = page ? (
    <div className="tg-table-wrap">
      {columnsLauncher}
      <table data-report-id="company.tagcatalogworkspace.t1" className="tg-table" data-part="tabla">
        <thead>{headerRow}</thead>
        <tbody>
          {shown(page.items).map((row) => {
            const marked = selected.has(row.tag.id);
            return (
              <tr key={row.tag.id} className={marked ? "tg-marked" : undefined}>
                <td className="tg-col-check">
                  {checkbox(marked, false, `${L("TITLE")}: ${row.tag.name}`, () => select(row.tag, !marked))}
                </td>
                <td className="tg-col-tag">
                  <span className="tg-tag-cell">
                    <ContactTagBadge name={row.tag.name} color={row.tag.color} match={term} />
                    {row.tag.archived && (current.state ?? "ACTIVE") === "ALL" ? (
                      <span className="tg-state-tag">{L("R1_ARCHIVED_TAG")}</span>
                    ) : null}
                  </span>
                </td>
                <td className="tg-col-description" title={row.tag.description ?? undefined}>
                  {row.tag.description ? (
                    <Highlighted text={row.tag.description} term={term} />
                  ) : (
                    <span className="tg-none">{L("NONE")}</span>
                  )}
                </td>
                {canContacts ? <td className="tg-col-contacts">{contactCell(row)}</td> : null}
                <td className="tg-col-created" title={createdTitle(row.tag.createdAt, locale)}>
                  {createdLabel(row.tag.createdAt, locale, C("TODAY"), C("YESTERDAY"))}
                </td>
                {rowOptionsAvailable(canManage, canExportContacts) ? (
                  <td className="tg-col-action">{rowMenu(row.tag)}</td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </table>
      <Pager
        page={page}
        L={L}
        onPage={(number) => changeQuery({ ...current, page: number }, false)}
        onSize={(size) => changeQuery({ ...current, pageSize: size, page: 1 }, false)}
      />
    </div>
  ) : null;

  // Celular: lista C17 con selección por pulsación larga o desde ⋮ › Seleccionar.
  const press = useRef<number | null>(null);
  const list = page ? (
    <ul className="tg-list" data-part="lista">
      {shown(rows).map((row, index) => {
        const marked = selected.has(row.tag.id);
        const contacts = row.contacts ?? 0;
        const date = createdLabel(row.tag.createdAt, locale, C("TODAY"), C("YESTERDAY"));
        return (
          <li
            key={row.tag.id}
            className={marked ? "tg-item tg-marked" : "tg-item"}
            ref={hasMore && index === Math.max(0, rows.length - 10) ? sentinel : undefined}
            onPointerDown={() => {
              if (press.current) clearTimeout(press.current);
              press.current = window.setTimeout(() => {
                setSelecting(true);
                select(row.tag, true);
              }, 500);
            }}
            onPointerUp={() => press.current && clearTimeout(press.current)}
            onPointerLeave={() => press.current && clearTimeout(press.current)}
            onPointerCancel={() => press.current && clearTimeout(press.current)}
            onContextMenu={(event) => event.preventDefault()}
          >
            {selecting || selected.size > 0 ? (
              <span className="tg-item-check">
                {checkbox(marked, false, `${L("TITLE")}: ${row.tag.name}`, () => select(row.tag, !marked))}
              </span>
            ) : null}
            <span className="tg-item-text">
              <span className="tg-item-line1">
                <ContactTagBadge name={row.tag.name} color={row.tag.color} match={term} />
                {row.tag.archived && (current.state ?? "ACTIVE") === "ALL" ? (
                  <span className="tg-state-tag">{L("R1_ARCHIVED_TAG")}</span>
                ) : null}
              </span>
              <span className="tg-item-line2">
                {canContacts ? (
                  <>
                    <span className="tg-item-count">
                      {contacts === 1 ? L("R1_LINE_ONE") : formatText(L("R1_LINE_MANY"), count(contacts))}
                    </span>
                    <span className="tg-item-date">{` · ${date}`}</span>
                  </>
                ) : (
                  date
                )}
              </span>
            </span>
            {rowMenu(row.tag)}
          </li>
        );
      })}
      {hasMore ? (
        <li className="tg-more">
          {moreFailed ? (
            <>
              <span>{C("LOAD_MORE_FAILED")}</span>
              <button type="button" className="tm-btn secondary small" onClick={retryMore}>
                {C("RETRY")}
              </button>
            </>
          ) : (
            <>
              <BheliLoader size={20} label={C("LOADING_MORE")} />
              <span>{C("LOADING_MORE")}</span>
            </>
          )}
        </li>
      ) : null}
    </ul>
  ) : null;

  return (
    <section className={`tg-workspace ${s}`} data-floating-lane="scroll" aria-busy={busy ? "true" : "false"} ref={root}>
      {header}
      {filters}
      {error ? <SectionMessage message={error} tone="error" /> : null}
      {busy && !page ? (
        <p className="bheli-loading tg-loading" role="status">
          <BheliLoader size={24} label={L("LOADING")} />
          {L("LOADING")}
        </p>
      ) : null}
      {emptyState}
      {page && !error && total > 0 ? (
        <>
          {tableBar}
          {mobile ? (
            <>
              {columnsLauncher}
              {/* P1 · R1: la tabla oculta va envuelta para que :has(>table[data-view-managed]) de feedback.css no
                  alcance al workspace (lo volvía un scroller lateral y la página se corría 12 px al abrir una hoja). */}
              <div className="tg-table-wrap" hidden>
                <table data-report-id="company.tagcatalogworkspace.t1" className="tg-table" hidden>
                  <thead>{headerRow}</thead>
                </table>
              </div>
              {list}
            </>
          ) : (
            table
          )}
        </>
      ) : null}

      {editor ? (
        <TeamWindow
          title={editor.tag ? L("R1_EDIT_TITLE") : L("NEW")}
          closeLabel={L("CLOSE")}
          open
          onDismiss={closeEditor}
          footer={
            <>
              {!mobile ? (
                <button type="button" className="tm-btn secondary" disabled={saving} onClick={closeEditor}>
                  {L("CANCEL")}
                </button>
              ) : null}
              <button
                type="button"
                className="tm-btn primary"
                disabled={saving}
                aria-busy={saving ? "true" : "false"}
                onClick={() => void save()}
              >
                {L("SAVE")}
              </button>
            </>
          }
        >
          <form
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              void save();
            }}
          >
            <TagEditorFields
              draft={draft}
              nameError={nameError}
              serverError={serverError}
              L={L}
              onChange={editDraft}
              nameRef={nameField}
            />
            {duplicateArchived && canManage ? (
              <button type="button" className="tm-btn secondary small" onClick={showArchived}>
                {L("ARCHIVED")} · {L("RESTORE")}
              </button>
            ) : null}
          </form>
        </TeamWindow>
      ) : null}
      <TeamWindow
        title={L("UNSAVED")}
        closeLabel={L("CLOSE")}
        confirm
        open={discarding}
        onDismiss={() => setDiscarding(false)}
        footer={
          <>
            <button type="button" className="tm-btn secondary" onClick={() => setDiscarding(false)}>
              {L("KEEP_EDITING")}
            </button>
            <button
              type="button"
              className="tm-btn primary"
              onClick={() => {
                setDiscarding(false);
                setEditor(null);
              }}
            >
              {L("DISCARD")}
            </button>
          </>
        }
      >
        {null}
      </TeamWindow>
      <TeamWindow
        title={L("EXPORT_TAGS")}
        closeLabel={L("CLOSE")}
        open={exporting}
        onDismiss={() => setExporting(false)}
        footer={
          <>
            {!mobile ? (
              <button type="button" className="tm-btn secondary" onClick={() => setExporting(false)}>
                {L("CANCEL")}
              </button>
            ) : null}
            <button type="button" className="tm-btn primary" disabled={extractBusy} onClick={() => void download()}>
              <ChatIcon name="download" />
              {L("DOWNLOAD")}
            </button>
          </>
        }
      >
        <p className="tg-export-line">
          {total === 1 ? L("R1_EXPORT_ONE") : formatText(L("R1_EXPORT_MANY"), count(total))}
        </p>
      </TeamWindow>
      {mobile ? (
        <SortSheet
          query={current}
          canContacts={canContacts}
          L={L}
          C={C}
          onApply={(sort, reverse) => changeQuery({ ...current, sort, reverse, page: 1 }, false)}
        />
      ) : null}
      <CopyFallbackWindow
        copy={copyFallback}
        mobile={mobile}
        L={L}
        C={C}
        onCopied={(done) => {
          setCopyFallback(null);
          copied(done);
        }}
        onClose={() => {
          setCopyFallback(null);
          requestAnimationFrame(() => {
            if (copyOpener.current?.isConnected) copyOpener.current.focus({ preventScroll: true });
          });
        }}
      />
    </section>
  );
}

const rowOptionsAvailable = (canManage: boolean, canExportContacts: boolean) => canManage || canExportContacts;

/** «1/10 – 15/10» o, si alguna fecha no es de este año, «1/12/25 – 15/1/26». */
function rangeText(from: string | null | undefined, to: string | null | undefined): string {
  const year = String(new Date().getFullYear());
  const sameYear = [from, to].every((day) => !day || day.startsWith(year));
  const show = (day: string | null | undefined) => {
    if (!day) return "…";
    if (sameYear) return shortDay(day);
    const [y, m, d] = day.split("-").map(Number) as [number, number, number];
    return `${d}/${m}/${String(y).slice(2)}`;
  };
  return `${show(from)} – ${show(to)}`;
}

function sortSummary(query: TagCatalogQuery, L: (key: string) => string, _C: (key: string) => string): string {
  const sort = query.sort ?? "CREATED";
  const column = sort === "NAME" ? L("TITLE") : sort === "USED" ? L("CONTACTS") : L("CREATED");
  const order =
    sort === "NAME"
      ? L(query.reverse ? "R1_SORT_ZA" : "R1_SORT_AZ")
      : sort === "USED"
        ? L(query.reverse ? "R1_SORT_LOW" : "R1_SORT_HIGH")
        : L(query.reverse ? "R1_SORT_OLDEST" : "R1_SORT_NEWEST");
  return `${column} · ${order}`;
}

/** Una opción de menú (C11): ícono de 18 px y texto; en el celular, opción de hoja de 48 px. */
function MenuOption({
  icon,
  label,
  detail,
  run,
  href,
  separator,
}: {
  icon: string;
  label: string;
  detail?: string;
  run: () => void;
  href?: string;
  separator?: boolean;
}) {
  const content = (
    <>
      <ChatIcon name={icon} />
      <span className="tg-option-text">
        {label}
        {detail ? <small>{detail}</small> : null}
      </span>
    </>
  );
  return (
    <>
      {separator ? <div className="tm-menu-divider" role="separator"></div> : null}
      {href ? (
        <a role="menuitem" href={href}>
          {content}
        </a>
      ) : (
        <button type="button" role="menuitem" onClick={run}>
          {content}
        </button>
      )}
    </>
  );
}

/** Buscador C06: píldora de 40 px con lupa, × solo con texto, Esc borra; filtra mientras se escribe. */
function TagSearch({
  inputRef,
  value,
  placeholder,
  clearLabel,
  onTyping,
  onChange,
}: {
  inputRef: React.RefObject<HTMLInputElement | null>;
  value: string;
  placeholder: string;
  clearLabel: string;
  onTyping: (value: string) => void;
  onChange: (value: string) => void;
}) {
  const [typed, setTyped] = useState(value);
  const [seen, setSeen] = useState(value);
  const timer = useRef<number | null>(null);
  if (seen !== value) {
    setSeen(value);
    setTyped(value);
  }
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );
  const apply = (next: string, now = false) => {
    setTyped(next);
    if (timer.current) clearTimeout(timer.current);
    onTyping(next);
    if (now) onChange(next);
    // C06 · decisión del dueño (29 oct 2026): 50 ms después de la última tecla; con 250 (y con 100) pasaba de 200 ms.
    if (!now) timer.current = window.setTimeout(() => onChange(next), 50);
  };
  return (
    <div className="tm-search tg-search" role="search" data-part="buscador">
      <ChatIcon name="search" />
      <input
        ref={inputRef}
        className="bheli-composite-input"
        type="search"
        enterKeyHint="search"
        aria-label={placeholder}
        placeholder={placeholder}
        value={typed}
        onChange={(event) => apply(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Escape" && typed) {
            event.preventDefault();
            apply("", true);
          }
        }}
      />
      {typed.length > 0 ? (
        <button
          type="button"
          className="tm-icon-btn"
          aria-label={clearLabel}
          title={clearLabel}
          onClick={() => {
            apply("", true);
            inputRef.current?.focus();
          }}
        >
          <ChatIcon name="x" />
        </button>
      ) : null}
    </div>
  );
}

/** «Más filtros»: el panel de C07 (hoja en el celular) con el grupo «Creación» y el rango de fechas de C34.
 * J8/J9 · C07 y C34 1.14.4: sin un rango aplicado, Desde y Hasta abren vacíos y el calendario sin días elegidos (E90).
 * Escritorio: el calendario aparece al tocar Desde o Hasta. Celular: Desde y Hasta son botones con aspecto de campo y
 * cambian la misma hoja a «← Elegir fechas» con un mes y su «Aplicar», que aplica toda la hoja y la cierra. */
const DateFilter = function DateFilter({
  ref,
  query,
  english,
  mobile,
  C,
  L,
  onApply,
  onClear,
}: {
  ref: React.Ref<FilterPanelHandle>;
  query: TagCatalogQuery;
  english: boolean;
  mobile: boolean;
  C: (key: string) => string;
  L: (key: string) => string;
  onApply: (from: string | null, to: string | null) => void;
  onClear: () => void;
}) {
  const today = todayDay();
  const panel = useRef<FilterPanelHandle>(null);
  useImperativeHandle(ref, () => ({ open: () => panel.current?.open(), close: () => panel.current?.close() }));
  const [choosing, setChoosing] = useState(false);
  // Escritorio: calendario a la vista. Celular: la subpantalla «← Elegir fechas» con lo que había al entrar.
  const [calendar, setCalendar] = useState<{ from: Day | null; to: Day | null } | null>(null);
  const [from, setFrom] = useState<Day | null>(query.from ?? null);
  const [to, setTo] = useState<Day | null>(query.to ?? null);
  const [error, setError] = useState<string | null>(null);
  const [opening, setOpening] = useState(0);
  const placeholder = L("R2_DATE_PLACEHOLDER");
  const typed = (text: string, start: boolean) => {
    const day = parseDay(text, english);
    if (!day) {
      setError(L("RANGE_ERROR"));
      return;
    }
    let first = start ? day : from,
      last = start ? to : day;
    if (first && last && first > last) [first, last] = [last, first];
    setFrom(first);
    setTo(last);
    setError(null);
  };
  const apply = () => {
    if (error) return;
    onApply(choosing ? from : null, choosing ? to : null);
    panel.current?.close();
  };
  const showCalendar = () => {
    if (calendar) return;
    setCalendar({ from, to });
    setOpening((value) => value + 1);
  };
  const rangeCalendar = (
    <RangeCalendar
      key={opening}
      inline
      maxDays={null}
      language={english ? "en-US" : "es-PE"}
      from={from}
      to={to}
      today={today}
      previousLabel={L("PREVIOUS")}
      nextLabel={L("NEXT")}
      dayOneLabel={C("DAY_ONE")}
      daysManyLabel={C("DAYS_MANY")}
      previousArrow={<ChatIcon name="chevron-left" />}
      nextArrow={<ChatIcon name="chevron-right" />}
      onChanged={(start, end) => {
        setFrom(start);
        setTo(end);
        setError(null);
      }}
    />
  );
  const field = (id: string, label: string, day: Day | null, start: boolean) =>
    mobile ? (
      <div className="tg-field">
        <span className="tg-label" id={`${id}-label`}>
          {label}
        </span>
        <button
          type="button"
          className="tg-date-button"
          aria-labelledby={`${id}-label ${id}`}
          id={id}
          onClick={showCalendar}
        >
          <span className={day ? undefined : "tg-date-empty"}>{day ? showDay(day, english) : placeholder}</span>
          <ChatIcon name="calendar" />
        </button>
      </div>
    ) : (
      <label className="tg-field" htmlFor={id} onFocus={showCalendar}>
        <span className="tg-label">{label}</span>
        <span className="tg-date">
          <DateInput
            id={id}
            errorId="tg-range-error"
            placeholder={placeholder}
            value={showDay(day, english)}
            invalid={false}
            onCommit={(text) => typed(text, start)}
          />
          <ChatIcon name="calendar" />
        </span>
      </label>
    );
  const subscreen = mobile && calendar !== null;
  return (
    <FilterPanel
      ref={panel}
      label={C("MORE_FILTERS")}
      clearLabel={C("CLEAR_FILTERS")}
      applyLabel={C("APPLY")}
      trigger={
        <>
          {C("MORE_FILTERS")}
          <ChatIcon name="chevron-down" />
        </>
      }
      footer={
        subscreen ? (
          <button type="button" className="tm-btn primary tg-wide" data-part="btn-principal" onClick={apply}>
            {C("APPLY")}
          </button>
        ) : undefined
      }
      onPreparing={() => {
        setFrom(query.from ?? null);
        setTo(query.to ?? null);
        // El panel y la hoja abren con «Elegir fechas» encendido y Desde y Hasta a la vista, vacíos si no hay rango
        // aplicado (referencias panel-mas-filtros-etiquetas y hoja-filtros-etiquetas): nunca un panel casi vacío.
        setChoosing(true);
        setCalendar(null);
        setError(null);
        setOpening((value) => value + 1);
      }}
      onApplying={apply}
      onClearing={onClear}
    >
      {subscreen ? (
        <section className="tg-date-screen" aria-label={C("CHOOSE_DATES")}>
          <div className="tg-sort-head">
            <button
              type="button"
              className="tm-icon-btn tg-sort-back"
              aria-label={L("R1_BACK")}
              title={L("R1_BACK")}
              onClick={() => {
                // ← vuelve a la hoja principal sin cambiar nada.
                setFrom(calendar.from);
                setTo(calendar.to);
                setCalendar(null);
              }}
            >
              <ChatIcon name="arrow-left" />
            </button>
            <strong className="tg-sheet-title">{C("CHOOSE_DATES")}</strong>
          </div>
          {rangeCalendar}
        </section>
      ) : (
        <section className="tg-filter-group">
          <h3>{L("CREATED")}</h3>
          <div className="tg-filter-options" role="group" aria-label={L("CREATED")}>
            <button
              type="button"
              className="tm-pill"
              aria-pressed={pressed(choosing)}
              onClick={() => {
                setChoosing(!choosing);
                setCalendar(null);
              }}
            >
              <ChatIcon name="calendar" />
              {C("CHOOSE_DATES")}
            </button>
          </div>
          {choosing ? (
            <div className="tg-range" role="group" aria-label={C("CHOOSE_DATES")}>
              <div className="tg-range-fields">
                {field("tg-from", C("FROM"), from, true)}
                {field("tg-to", C("TO"), to, false)}
              </div>
              {!mobile && calendar ? rangeCalendar : null}
              <FieldError id="tg-range-error" message={error} />
            </div>
          ) : null}
        </section>
      )}
    </FilterPanel>
  );
};

/** Paginado de C28 1.14.3: «Por página» con menú, el rango, flechas y números (con «…» si son más de 7). */
function Pager({
  page,
  L,
  onPage,
  onSize,
}: {
  page: TagCatalogPage;
  L: (key: string) => string;
  onPage: (page: number) => void;
  onSize: (size: number) => void;
}) {
  const menus = useMenus();
  const nav = useRef<HTMLElement>(null);
  const sizeOpen = useSyncExternalStore(menus.subscribe, () => menus.current() === "tags-page-size");
  // J7 · C11 1.14.4: el menú de «Por página» se abre debajo del botón, alineado a su borde izquierdo; arriba solo si
  // abajo no entra en la ventana. Va fijo a la ventana: el contenedor de la tabla (desplazamiento lateral) lo recortaba.
  useLayoutEffect(() => {
    if (!sizeOpen) return;
    const button = nav.current?.querySelector<HTMLElement>(".tg-size");
    const menu = nav.current?.querySelector<HTMLElement>(".tg-size-menu");
    if (!button || !menu || matchMedia(mobileQuery).matches) return;
    const box = button.getBoundingClientRect();
    const up = innerHeight - box.bottom < menu.offsetHeight + 8;
    Object.assign(menu.style, {
      position: "fixed",
      left: `${box.left}px`,
      top: up ? `${box.top - 6 - menu.offsetHeight}px` : `${box.bottom + 6}px`,
      bottom: "auto",
      // C11 1.14.4: la opción más larga, 12 px de aire y la columna del ✓ (88 px para 10 · 25 · 50); nunca menos que el botón.
      minWidth: `${Math.max(box.width, 88)}px`,
    });
  }, [sizeOpen]);
  const mode = pagerMode(page.total, page.pageSize);
  if (mode === "none") return null;
  const pages = Math.max(1, Math.ceil(page.total / page.pageSize));
  const numbers: (number | "…")[] = [];
  if (pages <= 7) for (let n = 1; n <= pages; n++) numbers.push(n);
  else {
    const near = [1, page.page - 1, page.page, page.page + 1, pages].filter((n) => n >= 1 && n <= pages);
    const unique = [...new Set(near)].sort((a, b) => a - b);
    unique.forEach((n, index) => {
      if (index > 0 && n - unique[index - 1]! > 1) numbers.push("…");
      numbers.push(n);
    });
  }
  const first = (page.page - 1) * page.pageSize + 1;
  const last = Math.min(page.page * page.pageSize, page.total);
  return (
    <nav className="tg-pager" aria-label={L("PAGE_SIZE")} data-part="paginado" ref={nav}>
      <span className="tg-pager-label">{L("PAGE_SIZE")}</span>
      <TeamMenu
        id="tags-page-size"
        className="tm-menu"
        triggerClass="tg-size"
        ariaLabel={L("PAGE_SIZE")}
        tooltip={L("PAGE_SIZE")}
        summary={
          <>
            {page.pageSize}
            <ChatIcon name="chevron-down" />
          </>
        }
      >
        <div className="tm-menu-items tg-size-menu" role="menu" aria-label={L("PAGE_SIZE")}>
          <BottomSheet>
            {[10, 25, 50].map((size) => (
              <button
                key={size}
                type="button"
                role="menuitemradio"
                aria-checked={pressed(size === page.pageSize)}
                onClick={() => {
                  menus.setOpen(null);
                  onSize(size);
                }}
              >
                <span className="tg-option-text">{size}</span>
                {size === page.pageSize ? <ChatIcon name="check" /> : null}
              </button>
            ))}
          </BottomSheet>
        </div>
      </TeamMenu>
      {mode === "full" ? (
        <>
      <span className="tg-range-text">{formatText(L("R1_RANGE"), count(first), count(last), count(page.total))}</span>
      <button
        type="button"
        className="tg-page"
        disabled={page.page <= 1}
        aria-label={L("PREVIOUS")}
        title={L("PREVIOUS")}
        onClick={() => onPage(page.page - 1)}
      >
        <ChatIcon name="chevron-left" />
      </button>
      {numbers.map((number, index) =>
        number === "…" ? (
          <span key={`gap-${index}`} className="tg-gap">
            …
          </span>
        ) : (
          <button
            key={number}
            type="button"
            className="tg-page"
            aria-current={number === page.page ? "page" : undefined}
            onClick={() => onPage(number)}
          >
            {number}
          </button>
        ),
      )}
      <button
        type="button"
        className="tg-page"
        disabled={page.page >= pages}
        aria-label={L("NEXT")}
        title={L("NEXT")}
        onClick={() => onPage(page.page + 1)}
      >
        <ChatIcon name="chevron-right" />
      </button>
        </>
      ) : null}
    </nav>
  );
}

/** C46 · ventana de respaldo cuando el navegador no deja copiar: ventana C30 de 480 px (pantalla completa en el
 * celular) con el texto en un campo de solo lectura ya seleccionado; «Copiar» vuelve a intentar con ese toque. */
function CopyFallbackWindow({
  copy,
  mobile,
  L,
  C,
  onCopied,
  onClose,
}: {
  copy: { text: string; count: number; fromSelection: boolean } | null;
  mobile: boolean;
  L: (key: string) => string;
  C: (key: string) => string;
  onCopied: (copy: { text: string; count: number; fromSelection: boolean }) => void;
  onClose: () => void;
}) {
  const field = useRef<HTMLTextAreaElement>(null);
  const select = () => {
    field.current?.focus({ preventScroll: true });
    field.current?.select();
  };
  // Excepción escrita en C46: al abrir, el foco va al campo con todo seleccionado (la tarea de la ventana es copiar).
  // TeamWindow ya puso el foco en la × (efecto del hijo, que corre antes); aquí se mueve al campo.
  useEffect(() => {
    if (copy) select();
  }, [copy]);
  const retry = async () => {
    if (!copy) return;
    try {
      await navigator.clipboard.writeText(copy.text);
      onCopied(copy);
      return;
    } catch {
      // Sigue con la copia del texto seleccionado.
    }
    select();
    let done = false;
    try {
      done = document.execCommand("copy");
    } catch {
      done = false;
    }
    if (done) onCopied(copy);
    else select();
  };
  const copyButton = (
    <button type="button" className={mobile ? "tm-btn primary tg-wide" : "tm-btn primary"} onClick={() => void retry()}>
      <ChatIcon name="copy" />
      {C("COPY")}
    </button>
  );
  return (
    <TeamWindow
      title={L("COPY")}
      closeLabel={L("CLOSE")}
      open={copy !== null}
      onDismiss={onClose}
      footer={
        mobile ? (
          copyButton
        ) : (
          <>
            <button type="button" className="tm-btn secondary" onClick={onClose}>
              {L("CLOSE")}
            </button>
            {copyButton}
          </>
        )
      }
    >
      <div className="tg-copy">
        <p className="tg-copy-line">{L(mobile ? "R2_COPY_FAILED_MOBILE" : "R2_COPY_FAILED")}</p>
        <textarea
          ref={field}
          className="tg-copy-text"
          readOnly
          wrap="off"
          spellCheck={false}
          aria-label={L("COPY")}
          value={copy?.text ?? ""}
        />
        <p className="tg-copy-meta">
          {copy?.count === 1 ? L("R2_COPY_READY_ONE") : formatText(L("R2_COPY_READY_MANY"), count(copy?.count ?? 0))}
        </p>
      </div>
    </TeamWindow>
  );
}

/** Celular · «Ordenar por» (C28): hoja inferior con la columna (✓), el orden en dos píldoras y «Aplicar»; tocar afuera o
 * Esc no cambian nada. Se abre desde el ⋮ de la lista (menus.setOpen("tags-sort")). */
function SortSheet({
  query,
  canContacts,
  L,
  C,
  onApply,
}: {
  query: TagCatalogQuery;
  canContacts: boolean;
  L: (key: string) => string;
  C: (key: string) => string;
  onApply: (sort: string, reverse: boolean) => void;
}) {
  const menus = useMenus();
  const open = useSyncExternalStore(menus.subscribe, () => menus.current() === "tags-sort");
  const [sort, setSort] = useState(query.sort ?? "CREATED");
  const [reverse, setReverse] = useState(query.reverse ?? false);
  const [wasOpen, setWasOpen] = useState(false);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setSort(query.sort ?? "CREATED");
      setReverse(query.reverse ?? false);
    }
  }
  const columns = [
    ["NAME", L("TITLE"), "R1_SORT_AZ", "R1_SORT_ZA"],
    ...(canContacts ? [["USED", L("CONTACTS"), "R1_SORT_HIGH", "R1_SORT_LOW"]] : []),
    ["CREATED", L("CREATED"), "R1_SORT_NEWEST", "R1_SORT_OLDEST"],
  ] as [string, string, string, string][];
  const chosen = columns.find((column) => column[0] === sort) ?? columns[columns.length - 1]!;
  return (
    <TeamMenu
      id="tags-sort"
      className="tm-menu tg-sort-host"
      triggerClass="tm-sr"
      ariaLabel={C("SORT_BY")}
      tooltip={C("SORT_BY")}
      summary={C("SORT_BY")}
    >
      <div className="tm-menu-items tg-sort-sheet" role="dialog" aria-label={C("SORT_BY")} data-part="ordenar">
        <BottomSheet>
          <div className="tg-sort-head">
            <button
              type="button"
              className="tm-icon-btn tg-sort-back"
              aria-label={L("R1_BACK")}
              title={L("R1_BACK")}
              onClick={() => menus.setOpen(null)}
            >
              <ChatIcon name="arrow-left" />
            </button>
            <strong className="tg-sheet-title">{C("SORT_BY")}</strong>
          </div>
          <div role="radiogroup" aria-label={C("SORT_BY")}>
            {columns.map(([value, label]) => (
              <button
                key={value}
                type="button"
                role="radio"
                className="tg-sort-column"
                aria-checked={pressed(sort === value)}
                onClick={() => {
                  setSort(value);
                  setReverse(false);
                }}
              >
                <span>{label}</span>
                {sort === value ? <ChatIcon name="check" /> : null}
              </button>
            ))}
          </div>
          <div className="tm-menu-divider" role="separator" />
          <div className="tg-sort-body">
          <h3>{C("ORDER_LABEL")}</h3>
          <div className="tm-pills" role="group" aria-label={C("ORDER_LABEL")}>
            {[false, true].map((value) => (
              <button
                key={String(value)}
                type="button"
                className="tm-pill"
                aria-pressed={pressed(reverse === value)}
                onClick={() => setReverse(value)}
              >
                {L(value ? chosen[3] : chosen[2])}
              </button>
            ))}
          </div>
          </div>
          <div className="tg-sort-bar">
          <button
            type="button"
            className="tm-btn primary tg-wide"
            onClick={() => {
              menus.setOpen(null);
              onApply(sort, reverse);
            }}
          >
            {C("APPLY")}
          </button>
          </div>
        </BottomSheet>
      </div>
    </TeamMenu>
  );
}
